import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Lightning, ArrowRight, Gift } from "@phosphor-icons/react";

export default function Signup() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [loading, setLoading] = useState(false);
    const [params] = useSearchParams();
    const refCode = params.get("ref")?.toUpperCase() || "";
    const { signup } = useAuth();
    const navigate = useNavigate();

    // Persist ref code across page reloads (in case user opens link, leaves, comes back)
    useEffect(() => {
        if (refCode) {
            localStorage.setItem("nexus_ref_code", refCode);
        }
    }, [refCode]);

    const effectiveRef = refCode || localStorage.getItem("nexus_ref_code") || null;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        try {
            await signup(email, password, displayName, effectiveRef);
            if (effectiveRef) localStorage.removeItem("nexus_ref_code");
            toast.success(
                effectiveRef
                    ? "SEAT RESERVED — REFERRER CREDITED. PROCEEDING TO CHECKOUT"
                    : "SEAT RESERVED — PROCEEDING TO CHECKOUT"
            );
            navigate("/checkout/cancel"); // Show pricing/checkout gate
        } catch (err) {
            toast.error(err.response?.data?.detail || "SIGNUP FAILED");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 grid-bg relative py-12">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background pointer-events-none" />
            <div className="relative w-full max-w-md">
                <Link to="/" data-testid="signup-logo" className="flex items-center gap-2 justify-center mb-10 group">
                    <div className="w-10 h-10 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={22} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-2xl">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </Link>

                <div className="glass-panel p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                    <p className="label-mono mb-3 text-electric-blue">FOUNDER COHORT</p>
                    <h1 className="font-heading text-3xl font-black tracking-tight mb-2">
                        Reserve your seat.
                    </h1>
                    <p className="text-sm text-muted-foreground mb-8">
                        Step 1 of 2: Create your operator profile.
                    </p>

                    {effectiveRef && (
                        <div
                            data-testid="signup-ref-banner"
                            className="bg-electric-blue/10 border border-electric-blue/40 px-3 py-2.5 mb-6 flex items-center gap-2"
                        >
                            <Gift size={14} weight="fill" className="text-electric-blue flex-shrink-0" />
                            <p className="text-xs text-electric-blue">
                                Referred by Operator <span className="font-mono font-bold">{effectiveRef}</span> — they'll get credited when you complete checkout.
                            </p>
                        </div>
                    )}

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div>
                            <label className="label-mono mb-2 block">CALLSIGN / DISPLAY NAME</label>
                            <input
                                type="text"
                                required
                                data-testid="signup-name-input"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue transition-all"
                                placeholder="e.g. SilentEcho_99"
                            />
                        </div>
                        <div>
                            <label className="label-mono mb-2 block">EMAIL</label>
                            <input
                                type="email"
                                required
                                data-testid="signup-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue transition-all"
                                placeholder="operator@nexus.lab"
                            />
                        </div>
                        <div>
                            <label className="label-mono mb-2 block">PASSWORD (MIN 6)</label>
                            <input
                                type="password"
                                required
                                data-testid="signup-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="signup-submit-btn"
                            className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-3 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_24px_rgba(0,123,255,0.5)] flex items-center justify-center gap-2"
                        >
                            {loading ? "RESERVING SEAT..." : "RESERVE & CONTINUE"}
                            {!loading && <ArrowRight size={16} weight="bold" />}
                        </button>
                    </form>

                    <p className="text-center mt-6 text-sm text-muted-foreground">
                        Already initiated?{" "}
                        <Link to="/login" data-testid="signup-login-link" className="text-electric-blue hover:underline">
                            Login here
                        </Link>
                    </p>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-6 leading-relaxed">
                    By creating an account you agree to our{" "}
                    <Link to="/terms" data-testid="signup-terms-link" className="text-electric-blue hover:underline">Terms</Link>{" "}
                    and{" "}
                    <Link to="/privacy" data-testid="signup-privacy-link" className="text-electric-blue hover:underline">Privacy Policy</Link>.
                </p>
                <p className="text-center text-xs text-muted-foreground mt-3 italic">
                    Step 2: Stripe Checkout will lock in your <span className="text-electric-blue">$20/mo</span> Founder rate.
                </p>
            </div>
        </div>
    );
}
